import * as React from 'react';
import {BaseFieldProps, Field, WrappedFieldProps} from 'redux-form';
import * as _ from 'lodash';
import {withStyles, WithStyles} from '@material-ui/core/styles';
import {Omit} from 'utility-types';
import {FormFieldProps} from './FormField';
import {Theme, createStyles} from '@material-ui/core/styles';
import * as classNames from 'classnames';
import DeleteIcon from '@material-ui/icons/Delete';
import IconButton from '@material-ui/core/IconButton/IconButton';
import Grid, {GridProps} from '@material-ui/core/Grid';
import {Breakpoint, keys as breakpoints} from '@material-ui/core/styles/createBreakpoints';
import {stringToDashed} from 'cl-automation-utils';

// TODO - fix the types - how can I declare the props of the component?
export interface ExtendedBaseFieldProps<NameType = string>
  extends Omit<BaseFieldProps<any> & FormFieldProps & Pick<GridProps, Breakpoint>, 'name'> {
  component: BaseFieldProps<any>['component'];
  name: NameType;
  label?: React.ReactNode;
  indent?: 1; // add more classes if you need more
  onDeleteField?: (e) => void;
  optional?: boolean;
  markOptional?: boolean;
}
export type ExtendedWrappedFieldProps = WrappedFieldProps & FormFieldProps & {label: React.ReactNode};

export type ExtendedWrappedFieldExternalProps<NameType = string> = {key?: string} & Omit<
  ExtendedBaseFieldProps<NameType>,
  'showErrors' | 'error' | 'touched'
>;

export interface FormFieldsProps {
  fields: ExtendedWrappedFieldExternalProps[];
  defaultGridItemProps?: GridProps;
  showErrors?: boolean;
  withPadding?: boolean;
}
const FormFields: React.FunctionComponent<FormFieldsProps & WithStyles<typeof styles>> = ({
  classes: {root, indent1, fieldWrapper, deleteButton, withPadding: withPaddingClass},
  fields,
  showErrors,
  withPadding,
  defaultGridItemProps,
}) => {
  return (
    <Grid container direction="column" className={classNames(root, {[withPaddingClass]: withPadding})}>
      {_.map(fields, ({indent, onDeleteField, optional, label, markOptional, ...props}) => {
        const gridProps = {...defaultGridItemProps, ..._.pick(props, breakpoints)};
        const idSuffix = props.key || props.name;
        const FieldComponent = (
          <Field
            id={`cla-field-input-${stringToDashed(idSuffix)}`}
            showErrors={showErrors}
            key={props.key || props.name}
            fullWidth={!_.isEmpty(gridProps)}
            label={optional && markOptional !== false ? `${label} (optional)` : label}
            {..._.omit(props, breakpoints)}
          />
        );
        return (
          <Grid
            className={classNames({[indent1]: indent === 1})}
            id={`cla-field-${stringToDashed(idSuffix)}`}
            item
            {...gridProps}
            key={props.key || props.name}
          >
            {onDeleteField ? (
              <div className={fieldWrapper}>
                {FieldComponent}
                <IconButton
                  id={`cla-btn-field-delete-${stringToDashed(idSuffix)}`}
                  className={deleteButton}
                  onClick={onDeleteField}
                >
                  <DeleteIcon id={`cla-icon-field-delete-${stringToDashed(idSuffix)}`} />
                </IconButton>
              </div>
            ) : (
              FieldComponent
            )}
          </Grid>
        );
      })}
    </Grid>
  );
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      flexWrap: 'wrap',
    },
    indent1: {
      marginLeft: theme.spacing(3.5),
    },
    deleteButton: {
      visibility: 'hidden',
      margin: theme.spacing(1),
    },
    fieldWrapper: {
      alignItems: 'center',
      display: 'flex',
      width: '100%',
      '&:hover > $deleteButton': {
        visibility: 'visible',
      },
    },
    withPadding: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
    },
  });

export default withStyles(styles)(FormFields);
